import React, { useEffect, useRef, useState } from 'react';

function TownAutocomplete({
  id,
  label,
  value = '',
  onChange,
  towns = [],
  loading = false,
  required = false,
  placeholder = 'Search town or city',
  inputClassName = 'w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100',
}) {
  const [query, setQuery] = useState(value);
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);
  const skipNextValueSync = useRef(false);
  const listboxId = `${id}-options`;
  const filteredTowns = towns.filter((town) => (
    String(town.name || town).toLowerCase().includes(query.toLowerCase())
  ));

  useEffect(() => {
    if (skipNextValueSync.current) {
      skipNextValueSync.current = false;
      return;
    }
    setQuery(value || '');
    setOpen(false);
    inputRef.current?.setCustomValidity('');
  }, [value]);

  const handleInputChange = (event) => {
    const nextQuery = event.target.value;
    setQuery(nextQuery);
    if (value) skipNextValueSync.current = true;
    onChange('');
    setOpen(Boolean(nextQuery));
    inputRef.current?.setCustomValidity(required && nextQuery ? 'Select a town from the suggestions.' : '');
  };

  const selectTown = (town) => {
    const townName = town.name || town;
    setQuery(townName);
    setOpen(false);
    inputRef.current?.setCustomValidity('');
    onChange(townName);
  };

  return (
    <div className="relative">
      <label htmlFor={id} className="mb-2 block text-sm font-medium text-slate-700">{label}</label>
      <input
        ref={inputRef}
        id={id}
        type="text"
        value={query}
        onChange={handleInputChange}
        onFocus={() => setOpen(Boolean(query) && !value)}
        onBlur={() => setOpen(false)}
        placeholder={loading ? 'Loading towns...' : placeholder}
        autoComplete="off"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={open && !value}
        aria-controls={listboxId}
        disabled={loading}
        required={required}
        className={inputClassName}
      />
      {open && !value && query && (
        <div id={listboxId} role="listbox" className="absolute z-20 mt-2 max-h-48 w-full overflow-y-auto rounded-2xl border border-slate-200 bg-white p-1 shadow-lg">
          {filteredTowns.length > 0 ? filteredTowns.map((town) => {
            const townName = town.name || town;
            return (
              <button
                key={town.id || townName}
                type="button"
                role="option"
                aria-selected={false}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => selectTown(town)}
                className="block w-full rounded-xl px-3 py-2 text-left text-sm text-slate-700 hover:bg-orange-50 hover:text-slate-900"
              >
                {townName}
              </button>
            );
          }) : (
            <p className="px-3 py-2 text-sm text-slate-500">No matching towns</p>
          )}
        </div>
      )}
    </div>
  );
}

export default TownAutocomplete;